import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Timeline } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  CompassParagraph,
  StyledAnchor,
} from "../../components/common/styledComponents";
import {
  blueText,
  greyText,
  lightGreys,
  statusBadgeColors,
} from "../../styles";
import { usePost } from "../../hooks/usePost";
import {
  TaskPriority,
  TaskStatus,
  TaskTrail,
  TrailPathItem,
  WsQParamType,
} from "../../models/workflowModels";
import { buildTrailPath } from "./utils";
import { ROUTES } from "../../routes/routeNames";
import { STATUS_MAPPING } from "./constants";
import { StatusBadge } from "../../components/visuals/StatusBadge";
import { PriorityIndicator } from "../../components/visuals/PriorityIndicator";
import { Avatar } from "../../components/visuals/Avatar";
import { BadgeStatus } from "../../types/StatusBadgeType";
import penIcon from "../../assets/svg/pen.svg";
import { Icon } from "../../components/visuals/styledComponents";
import { formatReadableDate } from "../../utils/formattingUtils";

// --- Styled Components ---

const TimelineContainer = styled.div`
  width: 100%;
  max-height: 300px;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px 0 0;
  margin-left: -16px;

  .ant-timeline {
    padding: 0;
  }
  .ant-timeline-item {
    padding-bottom: 8px;
  }
  .ant-timeline-item-label {
    width: 90px !important;
    text-align: right !important;
    font-size: 10px;
    color: ${greyText};
    padding-top: 3px;
  }
  .ant-timeline-item-tail,
  .ant-timeline-item-head {
    inset-inline-start: 104px !important;
  }
  .ant-timeline-item-head {
    width: 10px;
    height: 10px;
    border-width: 2px;
  }
  .ant-timeline-item-content {
    inset-inline-start: 104px !important;
    width: calc(100% - 128px) !important;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const EventCard = styled.div<{ clickable?: boolean; borderColor?: string }>`
  padding: 6px 10px;
  border-radius: 4px;
  border-left: 3px solid
    ${({ borderColor }): string => borderColor || lightGreys.borderLightGrey};
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  cursor: ${({ clickable }): string => (clickable ? "pointer" : "default")};
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: ${({ clickable }): string =>
      clickable
        ? "0 4px 8px rgba(0, 0, 0, 0.12)"
        : "0 2px 4px rgba(0, 0, 0, 0.08)"};
  }
`;

const EventHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DetailRow = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  margin-top: 4px;
`;

const NoteTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${blueText};
  font-family: "ApercuMedium";
  font-size: 12px;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

// --- Helpers ---

/** Extract status/priority from detail entries for inline badge rendering */
const extractBadgeFields = (
  item: TrailPathItem,
): { status?: TaskStatus; priority?: TaskPriority } => {
  let status: TaskStatus | undefined;
  let priority: TaskPriority | undefined;

  for (const entry of item.detailEntries) {
    if (entry.label === "Status") {
      const normalized = entry.value.replace(/\s+/g, "_").toUpperCase();
      const match = Object.values(TaskStatus).find(
        (s) => s === normalized || s.toUpperCase() === normalized,
      );
      if (match) status = match;
    }
    if (entry.label === "Priority") {
      const normalized = entry.value.replace(/\s+/g, "_").toUpperCase();
      const match = Object.values(TaskPriority).find(
        (p) => p === normalized || p.toUpperCase() === normalized,
      );
      if (match) priority = match;
    }
  }

  return { status, priority };
};

// --- Component ---

export const PathToResolution: React.FC = () => {
  const [trailPathData, setTrailPathData] = useState<TrailPathItem[]>([]);
  const [expandedIndex, setExpandedIndex] = useState<number>(-1);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { data, sendData: getTaskTrail } = usePost<
    TaskTrail[],
    { task_id: string }
  >();

  const taskId = searchParams.get("taskId");

  useEffect(() => {
    taskId &&
      getTaskTrail("GET_TASK_TRAIL", {
        task_id: taskId,
      });
  }, [taskId]);

  useEffect(() => {
    data && setTrailPathData(buildTrailPath(data));
  }, [data]);

  const navigateToAddNote = (): void => {
    if (!taskId) return;
    navigate({
      pathname: `${ROUTES.WORKSPACE}/${WsQParamType.ADD_NOTE}`,
      search: `?taskId=${taskId}`,
    });
  };

  const navigateToViewNote = (noteId: string): void => {
    const params = new URLSearchParams(searchParams);
    params.set("noteId", noteId);
    navigate({
      pathname: `${ROUTES.WORKSPACE}/${WsQParamType.VIEW_NOTE}`,
      search: `?${params.toString()}`,
    });
  };

  if (!taskId) return null;

  const timelineItems = trailPathData.map((item, index) => {
    const isExpanded = expandedIndex === index;
    const hasDetails = item.detailEntries.length > 0;
    const statusColor =
      statusBadgeColors[STATUS_MAPPING[item.status]]?.bg ||
      lightGreys.borderLightGrey;

    // For UPDATE events, extract status/priority to show badges instead of generic tag
    const isUpdate = item.actionType === "UPDATE_TASK";
    const badgeFields = isUpdate ? extractBadgeFields(item) : {};
    const hasBadges = !!(badgeFields.status || badgeFields.priority);

    // Filter out status/priority from detail entries since they're shown as badges
    const filteredDetails = isUpdate
      ? item.detailEntries.filter(
          (e) => e.label !== "Status" && e.label !== "Priority",
        )
      : item.detailEntries;

    return {
      color: statusBadgeColors[STATUS_MAPPING[item.status]]?.text || "#929395",
      label: formatReadableDate(item.date),
      children: (
        <EventCard
          clickable={hasDetails || item.isNoteEvent}
          borderColor={statusColor}
          onClick={(): void => {
            if (item.isNoteEvent && item.noteIds[0]) {
              navigateToViewNote(item.noteIds[0]);
              return;
            }
            if (hasDetails) setExpandedIndex(isExpanded ? -1 : index);
          }}
        >
          <EventHeader>
            {/* For updates with status/priority, show badges; otherwise no badge */}
            {isUpdate && hasBadges ? (
              <>
                {badgeFields.status && (
                  <StatusBadge
                    status={STATUS_MAPPING[badgeFields.status] as BadgeStatus}
                  />
                )}
                {badgeFields.priority && (
                  <PriorityIndicator priority={badgeFields.priority} />
                )}
              </>
            ) : null}

            {/* Note events: show pen icon + clickable title */}
            {item.isNoteEvent ? (
              <NoteTitle
                style={{
                  cursor: "pointer",
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                onClick={
                  item.noteIds[0]
                    ? (e: React.MouseEvent): void => {
                        e.stopPropagation();
                        navigateToViewNote(item.noteIds[0]);
                      }
                    : undefined
                }
              >
                <Icon
                  src={penIcon}
                  alt="Note"
                  style={{ width: 12, height: 12, flexShrink: 0 }}
                />
                {item.children[0]}
              </NoteTitle>
            ) : (
              <CompassParagraph
                fontSize={12}
                style={{
                  margin: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {item.children[0]}
              </CompassParagraph>
            )}
            <Avatar username={item.performedBy} size="small" tooltip={true} />
          </EventHeader>

          {/* Additional children (grouped note entries) */}
          {item.children.length > 1 &&
            item.children.slice(1).map((child, ci) => {
              const childNoteId = item.noteIds[ci + 1];
              return (
                <NoteTitle
                  key={ci}
                  style={{ marginTop: 4 }}
                  onClick={
                    childNoteId
                      ? (e: React.MouseEvent): void => {
                          e.stopPropagation();
                          navigateToViewNote(childNoteId);
                        }
                      : undefined
                  }
                >
                  <Icon
                    src={penIcon}
                    alt="Note"
                    style={{ width: 12, height: 12, flexShrink: 0 }}
                  />
                  {child}
                </NoteTitle>
              );
            })}

          {/* Remaining detail entries (excluding status/priority already shown as badges) */}
          {isExpanded && filteredDetails.length > 0 && (
            <div
              style={{
                marginTop: 8,
                paddingTop: 6,
                borderTop: `1px solid ${lightGreys.borderLightGrey}`,
              }}
            >
              {filteredDetails.map((entry, di) => (
                <DetailRow key={di}>
                  <CompassParagraph fontSize={11} color={greyText}>
                    {entry.label}:
                  </CompassParagraph>
                  {entry.label === "Assigned To" ? (
                    <Avatar
                      username={entry.value}
                      size="small"
                      tooltip={true}
                    />
                  ) : (
                    <CompassParagraph fontSize={11}>
                      {entry.value}
                    </CompassParagraph>
                  )}
                </DetailRow>
              ))}
            </div>
          )}
        </EventCard>
      ),
    };
  });

  return (
    <>
      <SectionHeader>
        <CompassParagraph bold={true}>Path to Resolution</CompassParagraph>
        <StyledAnchor
          onClick={navigateToAddNote}
          hoverUnderline={true}
          color={blueText}
        >
          <CompassParagraph
            fontSize={12}
            color={blueText}
            cursor="pointer"
            hoverUnderline={true}
          >
            + Add Note
          </CompassParagraph>
        </StyledAnchor>
      </SectionHeader>
      <TimelineContainer>
        <Timeline mode="left" items={timelineItems} />
      </TimelineContainer>
    </>
  );
};
